// The Content Filler's overlay (/api/place-edits), applied to the authored
// places from places.json.
//
// ## ⚠️ ONE IMPLEMENTATION, meant to be mirrored — not rewritten
//
// This is the only code that decides what an edited place looks like. The
// website runs it in PathfinderPage as the snapshot loads; the phone app should
// copy THIS FILE byte-for-byte into its own mirror folder (the way it already
// mirrors `src/utils/eventTime.js`) rather than re-deriving the rules, because
// two merges that disagree mean a volunteer fixes the hours and only one of the
// two clients believes them. It is pure — no browser API, no import — for
// exactly that reason. Pinned by `node src/utils/placeEdits.test.mjs`.
//
// ## The rules, all of them
//
//  · FIELD-LEVEL. A key present in `fields` replaces the authored value, even
//    when it is empty ("" hours means "we no longer know", said on purpose). A
//    key absent leaves the authored value alone — which is what lets a later
//    UMapsTools update flow through for everything a volunteer did not touch.
//  · MENU per ITEM. `menu.items[id]` patches that authored item field by field;
//    `menu.removed` hides authored items by id; `menu.added` appends items that
//    exist only here. A patch naming an item the authored menu no longer has is
//    ignored, not resurrected.
//  · `hiddenImages` drops authored photos by exact path. Photos a volunteer
//    ADDED are not here at all — they ride the place-photo overlay
//    (`mergePlacePhotos`), which both clients already apply.
//  · Nothing is mutated. A place with no edit comes back as the SAME object, so
//    a memo keyed on it does not bust.

const MENU_ITEM_FIELDS = ['name', 'price', 'category', 'notes', 'calories', 'photo', 'tags'];

function applyMenu(items, menu) {
  const list = Array.isArray(items) ? items : [];
  if (!menu || typeof menu !== 'object') return list;
  const removed = new Set(Array.isArray(menu.removed) ? menu.removed : []);
  const patches = menu.items && typeof menu.items === 'object' ? menu.items : {};
  const out = [];
  for (const item of list) {
    if (!item || removed.has(item.id)) continue;
    const patch = patches[item.id];
    if (!patch) { out.push(item); continue; }
    const next = { ...item };
    for (const f of MENU_ITEM_FIELDS) if (Object.prototype.hasOwnProperty.call(patch, f)) next[f] = patch[f];
    out.push(next);
  }
  for (const a of Array.isArray(menu.added) ? menu.added : []) if (a && a.name) out.push(a);
  return out;
}

/** One place + its overlay entry → the place a reader should see. */
export function applyPlaceEdit(place, edit) {
  if (!place || !edit || typeof edit !== 'object') return place;
  const next = { ...place };
  const fields = edit.fields && typeof edit.fields === 'object' ? edit.fields : {};
  for (const [k, v] of Object.entries(fields)) next[k] = v;
  if (edit.menu) {
    next.menuItems = applyMenu(place.menuItems, edit.menu);
    // A category that only an ADDED item uses must still exist, or the item
    // lands under no heading on a menu that is browsed by heading.
    const cats = Array.isArray(next.menuCategories) ? [...next.menuCategories] : [];
    for (const it of next.menuItems) if (it.category && !cats.includes(it.category)) cats.push(it.category);
    next.menuCategories = cats;
  }
  if (Array.isArray(edit.hiddenImages) && edit.hiddenImages.length && Array.isArray(place.images)) {
    const hide = new Set(edit.hiddenImages);
    next.images = place.images.filter((u) => !hide.has(u));
  }
  return next;
}

/** Every place, with `edits` ({ [placeId]: entry }) applied. */
export function applyPlaceEdits(places, edits) {
  if (!Array.isArray(places) || !edits || typeof edits !== 'object') return places;
  if (!Object.keys(edits).length) return places;
  return places.map((p) => (p && edits[p.id] ? applyPlaceEdit(p, edits[p.id]) : p));
}
